export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  hint?: string | null;
  personaId?: string | null;
  personaName?: string | null;
  userId?: string | null;
  userName?: string | null;
  createdAt: string;
}

export interface Evaluation {
  goalScore: number;
  goalReached: boolean;
  /** Omitted when the viewer only has summary access to the session. */
  summary?: string;
  strengths?: string[];
  improvements?: string[];
}

export type RecordingVisibility = 'rep_only' | 'rep_and_managers' | 'named_viewers';

/** `full` = transcript + written feedback; `summary` = metadata + score only. */
export type RecordingAccess = 'full' | 'summary';

export interface SessionViewer {
  userId: string;
  userName: string | null;
  userEmail: string;
}

export interface PersonaSummary {
  id: string;
  name: string;
  jobTitle: string;
  type: string;
  source?: string | null;
  isPrimary: boolean;
  /** Session-scoped TTS voice id (e.g. OpenAI), when assigned. */
  voice?: string | null;
  /** Stored gender from Persona row; used for face-tile and voice alignment. */
  gender?: string | null;
}

export interface Participant {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string;
  role: string;
  joinedAt: string;
}

export interface SessionInfo {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string;
  personaName: string;
  personaJobTitle?: string;
  /** From Persona.source; `ai_generated` when org AI persona. */
  personaSource?: string | null;
  goalLabel: string | null;
  goalDescription: string | null;
  pitchStyle: string;
  mode: 'verbal' | 'text';
  visibility?: 'private' | 'org_open';
  recordingVisibility?: RecordingVisibility;
  access?: RecordingAccess;
  /** Named viewers; only returned to the rep. */
  viewers?: SessionViewer[];
  timeLimitMinutes: number;
  createdAt: string | null;
  /** When the rep began the practice session (may be null for older rows). */
  startedAt?: string | null;
  /** When the session was ended (completed). */
  endedAt?: string | null;
  /** Wall-clock duration when both started and ended are known. */
  durationSeconds?: number | null;
  status: string;
  /** Set when invite emails were last sent successfully for this session. */
  inviteEmailsSentAt?: string | null;
  personas?: PersonaSummary[];
  participants?: Participant[];
  isParticipant?: boolean;
  /** Org has Tavus CVI enabled (platform admin). */
  tavusEnabled?: boolean;
  /** Persona id that receives the Tavus video tile when a conversation exists. */
  tavusVideoPersonaId?: string | null;
  /** True when the server has provisioned a Tavus conversation for this session. */
  tavusConversationActive?: boolean;
}

export interface SessionUsage {
  inputTokens: number;
  outputTokens: number;
  callCount: number;
  costUsd?: number;
}

export interface SessionViewProps {
  sessionInfo: SessionInfo;
  messages: Message[];
  evaluation: Evaluation | null;
  loading: boolean;
  isCompleted: boolean;
  isOwnSession: boolean;
  remainingSeconds: number | null;
  ending: boolean;
  sessionUsage?: SessionUsage | null;
  participants?: Participant[];
  speakingUsers?: Set<string>;
  onSendMessage: (content: string) => Promise<void>;
  onEndSession: () => void;
  onBack: () => void;
  postSpeaking?: (speaking: boolean) => Promise<void>;
  /** Profile preference: when true, transcript column opens on md+ viewports (see getInitialShowTranscript). */
  showTranscriptByDefault?: boolean;
  /** Profile preference: when true, verbal persona tiles show AI-generated face images. */
  showPersonaFaces?: boolean;
}

const PERSONA_COLORS = [
  { bg: 'bg-gray-100', text: 'text-gray-900', label: 'text-gray-600', ring: 'ring-gray-400' },
  { bg: 'bg-blue-50', text: 'text-blue-900', label: 'text-blue-600', ring: 'ring-blue-400' },
  {
    bg: 'bg-purple-50',
    text: 'text-purple-900',
    label: 'text-purple-600',
    ring: 'ring-purple-400',
  },
  { bg: 'bg-amber-50', text: 'text-amber-900', label: 'text-amber-600', ring: 'ring-amber-400' },
  { bg: 'bg-teal-50', text: 'text-teal-900', label: 'text-teal-600', ring: 'ring-teal-400' },
];

export function getPersonaColor(index: number) {
  return PERSONA_COLORS[index % PERSONA_COLORS.length];
}
